/**
 * Centralized Authentication Actions (Redux Thunks)
 *
 * Consolidates ALL auth flow thunks:
 * - Login
 * - Logout
 * - OTP Request/Verification
 * - Password Reset
 * - Agreement Sign
 * - First Login Password Change
 * - Token Refresh
 *
 * Pattern: Each thunk returns only the response data (NO tokens)
 * Tokens are stored in reducers via tokenManager
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { tr } from '../../i18n/i18n';
import { authService } from '../../services/auth';
import {
  ILoginRequest,
  ILoginThunkReturn,
  IRequestOtpEmailRequestBody,
  IRequestOtpThunkReturn,
  IVerifyOtpRequestBody,
  IVerifyOtpThunkReturn,
  IResetPasswordRequestBody,
  IResetPasswordThunkReturn,
  ISubmitAgreementSignRequest,
  ISubmitAgreementSignThunkReturn,
  IChangePasswordFirstLoginRequest,
  IChangePasswordFirstLoginThunkReturn,
  IRequestMeVerificationRequestBody,
  IRequestMeVerificationResponseBody,
  IRefreshTokenThunkReturn,
  ISelectOrganizationThunkReturn,
  ISelectOrganizationRequest,
  ISelectOrganizationByStudentRequest,
  IAuthOrganizationOption,
  IVerifyTwoFactorRequest,
  IAgreementOrganization,
  IGuardianStudent,
} from '../../interface/auth.interface';
import { IErrorResponse } from '../../interface/main.interface';
import { normalizeAxiosError } from '../../utility/axiosErrorHandler';
import { AUTH_ACTION_CODES } from '../../constant/auth.constant';

/**
 * ============================================================================
 * LOGIN THUNK
 * ============================================================================
 */

export const loginUser = createAsyncThunk<
  ILoginThunkReturn,
  ILoginRequest,
  { rejectValue: IErrorResponse }
>('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const response = await authService.login(credentials);
    const accessToken = response.headers['accesstoken'];
    const refreshToken = response.headers['refreshtoken'];
    const changePasswordToken = response.headers['x-change-password-token'];
    const publicToken = response.headers['x-public-token'];
    // Short-lived (~5 min) temp token issued when the user belongs to multiple
    // organizations (ORGANIZATION_SELECTION_REQUIRED flow)
    const tempToken = response.headers['temptoken'];
    // CVA-3748: short-lived token carrying the login across the 2FA challenge.
    const twoFaToken = response.headers['x-2fa-token'];

    // Extract action code from response for navigation logic (centralized in action layer)
    // For intermediate steps: response.data.data.status = 'AGREEMENT_SIGN_REQUIRED' | 'FIRST_LOGIN_PASSWORD_CHANGE_REQUIRED' | 'ORGANIZATION_SELECTION_REQUIRED'
    // For successful complete login: response.data has role and profile at root (no nested data.status)
    const actionCode = response.data?.data?.status;

    // Extract agreement pre-fill data for AGREEMENT_SIGN_REQUIRED flow (Dynamic Editable Document)
    // Backend returns organizations array (even single-org returns array of 1) and user object
    const responseData = response.data?.data;
    const agreementPreFill =
      responseData?.organizations && responseData?.user
        ? {
            organizations: responseData.organizations as IAgreementOrganization[],
            user: responseData.user,
            agreementDate: responseData.agreementDate || new Date().toISOString().split('T')[0],
          }
        : undefined;

    // ORGANIZATION_SELECTION_REQUIRED flow: lightweight organization list for
    // the organization-selection page (any multi-org user, not guardian-only)
    const organizations =
      actionCode === AUTH_ACTION_CODES.ORGANIZATION_SELECTION_REQUIRED
        ? (responseData?.organizations as IAuthOrganizationOption[])
        : undefined;

    // CVA-3748: masked email + reason, rendered on the OTP screen so the user
    // knows where the code went and why they were challenged.
    const twoFactorData = responseData as
      | { email?: string; reason?: string; expiresInSeconds?: number }
      | undefined;
    const twoFactorChallenge =
      actionCode === AUTH_ACTION_CODES.TWO_FACTOR_REQUIRED
        ? {
            email: twoFactorData?.email || '',
            reason: twoFactorData?.reason || '',
            expiresInSeconds: twoFactorData?.expiresInSeconds || 0,
          }
        : undefined;

    return {
      response: response.data,
      accessToken,
      refreshToken,
      publicToken: changePasswordToken ?? publicToken, // Rename on frontend
      tempToken,
      twoFaToken,
      actionCode, // Return action code directly for page layer consumption
      agreementPreFill, // Pre-fill data for agreement modal (WYSIWYS)
      organizations,
      twoFactorChallenge,
    };
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, tr('g.login_failed_2', 'Login failed.')));
  }
});

/**
 * ============================================================================
 * ORGANIZATION SELECTION THUNK (login flow — temp token)
 * ============================================================================
 */

/**
 * Exchange the short-lived temp token (issued by /auth/login for multi-org
 * users) for real org-scoped tokens. New tokens come back in response headers;
 * the body is a full auth response like a complete login.
 */
/**
 * CVA-3748 — verify the two-factor code and finish signing in.
 *
 * Returns the same union the login thunk does: tokens for a single-org user, or a
 * tempToken plus ORGANIZATION_SELECTION_REQUIRED for a multi-org one. The page
 * reuses `handleAuthAction`, so the post-verify routing is shared with login.
 */
export const verifyTwoFactor = createAsyncThunk<
  ILoginThunkReturn,
  IVerifyTwoFactorRequest & { twoFaToken: string },
  { rejectValue: IErrorResponse }
>('auth/verify-two-factor', async ({ twoFaToken, ...payload }, { rejectWithValue }) => {
  try {
    const response = await authService.verifyTwoFactor(payload, twoFaToken);
    const accessToken = response.headers['accesstoken'];
    const refreshToken = response.headers['refreshtoken'];
    const tempToken = response.headers['temptoken'];
    const actionCode = response.data?.data?.status;
    const responseData = response.data?.data;

    return {
      response: response.data,
      accessToken,
      refreshToken,
      tempToken,
      actionCode,
      organizations:
        actionCode === AUTH_ACTION_CODES.ORGANIZATION_SELECTION_REQUIRED
          ? (responseData?.organizations as IAuthOrganizationOption[])
          : undefined,
    };
  } catch (err) {
    return rejectWithValue(
      normalizeAxiosError(err, 'Failed to verify the code. Please try again.'),
    );
  }
});

/** CVA-3748 — request a fresh code; the previous one stops working immediately. */
export const resendTwoFactorCode = createAsyncThunk<
  { email: string; expiresInSeconds: number },
  { twoFaToken: string },
  { rejectValue: IErrorResponse }
>('auth/resend-two-factor', async ({ twoFaToken }, { rejectWithValue }) => {
  try {
    const response = await authService.resendTwoFactorCode(twoFaToken);
    return response.data?.data;
  } catch (err) {
    return rejectWithValue(
      normalizeAxiosError(err, 'Failed to send a new code. Please try again.'),
    );
  }
});

export const selectOrganization = createAsyncThunk<
  ISelectOrganizationThunkReturn,
  ISelectOrganizationRequest & { tempToken: string },
  { rejectValue: IErrorResponse }
>('auth/select-organization', async ({ organizationId, tempToken }, { rejectWithValue }) => {
  try {
    const response = await authService.selectOrganization({ organizationId }, tempToken);
    const accessToken = response.headers['accesstoken'];
    const refreshToken = response.headers['refreshtoken'];

    return {
      response: response.data,
      accessToken,
      refreshToken,
    };
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, 'Failed to select organization.'));
  }
});

/**
 * ============================================================================
 * ORGANIZATION SELECTION BY STUDENT THUNK (guardian student switch)
 * ============================================================================
 */

/**
 * Guardian picks a student — the backend issues a NEW org-scoped
 * accessToken/refreshToken pair (headers) plus a full auth response body.
 */
export const selectOrganizationByStudent = createAsyncThunk<
  ISelectOrganizationThunkReturn,
  ISelectOrganizationByStudentRequest,
  { rejectValue: IErrorResponse }
>('auth/select-organization-by-student', async (data, { rejectWithValue }) => {
  try {
    const response = await authService.selectOrganizationByStudent(data);
    const accessToken = response.headers['accesstoken'];
    const refreshToken = response.headers['refreshtoken'];

    return {
      response: response.data,
      accessToken,
      refreshToken,
    };
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, 'Failed to switch student.'));
  }
});

/**
 * ============================================================================
 * GUARDIAN STUDENTS THUNK (sidebar organization + student selector)
 * ============================================================================
 */

export const fetchGuardianStudents = createAsyncThunk<
  IGuardianStudent[],
  void,
  { rejectValue: IErrorResponse }
>('auth/fetch-guardian-students', async (_, { rejectWithValue }) => {
  try {
    const response = await authService.getGuardianStudents();
    return response.data?.data?.students || [];
  } catch (err) {
    return rejectWithValue(normalizeAxiosError(err, 'Failed to load students.'));
  }
});

/**
 * ============================================================================
 * OTP REQUEST THUNK
 * ============================================================================
 */

export const requestOtpEmail = createAsyncThunk<
  IRequestOtpThunkReturn,
  IRequestOtpEmailRequestBody,
  { rejectValue: IErrorResponse }
>('auth/request-otp-email', async (data, { rejectWithValue }) => {
  try {
    const response = await authService.requestOtpEmail(data);
    // Extract publicToken from response headers (for next OTP verification step)
    const publicToken = response.headers['accesstoken'];

    return {
      response: response.data,
      publicToken: publicToken || '',
    };
  } catch (err) {
    return rejectWithValue(
      normalizeAxiosError(err, tr('g.failed_to_request_otp', 'Failed to request OTP.')),
    );
  }
});

/**
 * ============================================================================
 * REQUEST USER VERIFICATION THUNK
 * ============================================================================
 */

export const requestUserVerification = createAsyncThunk<
  IRequestMeVerificationResponseBody,
  IRequestMeVerificationRequestBody,
  { rejectValue: IErrorResponse }
>('auth/request-user-verification', async (data, { rejectWithValue }) => {
  try {
    const response = await authService.requestUserVerification(data);
    return response.data;
  } catch (err) {
    return rejectWithValue(
      normalizeAxiosError(
        err,
        tr('g.failed_to_request_verification', 'Failed to request verification.'),
      ),
    );
  }
});

/**
 * ============================================================================
 * VERIFY OTP THUNK
 * ============================================================================
 */

export const verifyOtp = createAsyncThunk<
  IVerifyOtpThunkReturn,
  IVerifyOtpRequestBody,
  { rejectValue: IErrorResponse }
>('auth/verify-otp', async (data, { rejectWithValue }) => {
  try {
    const response = await authService.verifyOtp(data);
    // Extract publicToken from response headers (for password reset step)
    const publicToken = response.headers['accesstoken'];

    return {
      response: response.data,
      publicToken: publicToken || '',
    };
  } catch (err) {
    return rejectWithValue(
      normalizeAxiosError(err, tr('g.failed_to_verify_otp', 'Failed to verify OTP.')),
    );
  }
});

/**
 * ============================================================================
 * RESET PASSWORD THUNK
 * ============================================================================
 */

export const resetPassword = createAsyncThunk<
  IResetPasswordThunkReturn,
  IResetPasswordRequestBody,
  { rejectValue: IErrorResponse }
>('auth/reset-password', async (data, { rejectWithValue }) => {
  try {
    const response = await authService.resetPassword(data);
    return {
      response: response.data,
    };
  } catch (err) {
    return rejectWithValue(
      normalizeAxiosError(err, tr('g.failed_to_reset_password', 'Failed to reset password.')),
    );
  }
});

/**
 * ============================================================================
 * SUBMIT AGREEMENT SIGN (Signature Submission) THUNK
 * ============================================================================
 */

export const submitAgreementSign = createAsyncThunk<
  ISubmitAgreementSignThunkReturn,
  ISubmitAgreementSignRequest,
  { rejectValue: IErrorResponse }
>('auth/submit-agreement-sign', async (data, { rejectWithValue }) => {
  try {
    const response = await authService.submitAgreementSign(data);
    // Backend only returns X-Change-Password-Token for password change (see auth.controller.ts line 382)
    // NO accessToken/refreshToken returned during agreement sign
    const publicToken = response.headers['x-change-password-token'];

    // Extract action code from response for navigation logic (centralized in action layer)
    const actionCode = response.data?.data?.status;

    return {
      response: response.data,
      publicToken: publicToken || '', // Only publicToken for first login password change
      actionCode, // Return action code directly for page layer consumption
    };
  } catch (err) {
    return rejectWithValue(
      normalizeAxiosError(err, tr('g.failed_to_submit_agreement', 'Failed to submit agreement.')),
    );
  }
});

/**
 * ============================================================================
 * CHANGE PASSWORD FIRST LOGIN THUNK
 * ============================================================================
 */

export const changePasswordFirstLogin = createAsyncThunk<
  IChangePasswordFirstLoginThunkReturn,
  IChangePasswordFirstLoginRequest,
  { rejectValue: IErrorResponse }
>('auth/change-password-first-login', async (data, { rejectWithValue }) => {
  try {
    const response = await authService.changePasswordFirstLogin(data);
    const accessToken = response.headers['accesstoken'];
    const refreshToken = response.headers['refreshtoken'];

    return {
      response: response.data,
      accessToken,
      refreshToken,
    };
  } catch (err) {
    return rejectWithValue(
      normalizeAxiosError(err, tr('g.failed_to_change_password', 'Failed to change password.')),
    );
  }
});

/**
 * ============================================================================
 * LOGOUT THUNK
 * ============================================================================
 */

export const logOut = createAsyncThunk<void, void, { rejectValue: IErrorResponse }>(
  'auth/logout',
  async () => {
    try {
      await authService.userLogout();
      // Logout successful
    } catch (err) {
      // Log error but don't fail - user logout always clears tokens locally
      console.error('[auth] Logout error:', err);
      // Return silently - logout always succeeds locally even if server call fails
    }
  },
);

/**
 * ============================================================================
 * GET REFRESH TOKEN THUNK
 * ============================================================================
 */

export const getRefreshToken = createAsyncThunk<
  IRefreshTokenThunkReturn,
  string, // refreshToken parameter
  { rejectValue: IErrorResponse }
>('auth/refresh-token', async (refreshToken, { rejectWithValue }) => {
  try {
    const response = await authService.refreshToken(refreshToken);
    const accessToken = response.headers['accesstoken'];
    const newRefreshToken = response.headers['refreshtoken'];

    return {
      accessToken,
      refreshToken: newRefreshToken,
    };
  } catch (err) {
    return rejectWithValue(
      normalizeAxiosError(err, tr('g.failed_to_refresh_tokens', 'Failed to refresh tokens.')),
    );
  }
});

/**
 * ============================================================================
 * EXPORTS
 * ============================================================================
 */

export const authActions = {
  loginUser,
  selectOrganization,
  selectOrganizationByStudent,
  fetchGuardianStudents,
  requestOtpEmail,
  requestUserVerification,
  verifyOtp,
  resetPassword,
  submitAgreementSign,
  changePasswordFirstLogin,
  logOut,
  getRefreshToken,
};

export default authActions;
